/**
 * Promotion Engine - Core State Manager
 * Coordinates data parsing, gift additions, progress bar fills, and cart summaries.
 */
class LcPromotionEngine {
  constructor() {
    this.promotions = [];
    this.currentCart = null;
    this.unlockedStates = {}; // Track promoId -> unlocked state transitions
  }

  /**
   * Initialize Promotion Engine. Called on DOMContentLoaded.
   */
  init() {
    // 1. Load active, sorted promotions using Loader
    if (window.LcPromotionLoader) {
      this.promotions = window.LcPromotionLoader.load();
    } else {
      console.error('Promotion Engine - LcPromotionLoader class not found.');
      return;
    }

    // 2. Bind AJAX Cart update event listener
    document.addEventListener('lc:cart:updated', (e) => {
      this.evaluate(e.detail);
    });

    // 3. Fetch initial cart to trigger starting evaluations
    this.fetchInitialCart();
  }

  /**
   * Fetch initial cart state from /cart.js on page load.
   */
  async fetchInitialCart() {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      this.evaluate(cart);
    } catch (e) {
      console.error('Promotion Engine - Failed to fetch initial cart:', e);
    }
  }

  /**
   * Main evaluation loop triggered on load or cart update.
   * @param {object} cart - Shopify Cart object
   */
  evaluate(cart) {
    if (!cart) return;
    this.currentCart = cart;

    // 1. Sync free gifts (Add qualified gifts / Remove unqualified ones)
    if (window.LcPromotionGift) {
      window.LcPromotionGift.syncGifts(cart, this.promotions);
    }

    // 2. Evaluate and update all progress indicators & widgets
    this.promotions.forEach(promo => {
      const widgets = document.querySelectorAll(`[data-promo-widget="${promo.id}"]`);
      if (widgets.length === 0) return;

      const stats = window.LcPromotionProgress
        ? window.LcPromotionProgress.calculate(cart, promo)
        : { percentage: 0, message: '', isQualified: false };

      // Manage transitions between locked and unlocked states for unlock animations
      const wasUnlocked = !!this.unlockedStates[promo.id];
      if (stats.isQualified && !wasUnlocked) {
        this.unlockedStates[promo.id] = true;
        this.triggerUnlockAnimation(promo);
      } else if (!stats.isQualified) {
        this.unlockedStates[promo.id] = false;
      }

      // Update widget DOM fills
      widgets.forEach(widget => {
        if (window.LcPromotionProgress) {
          window.LcPromotionProgress.updateWidgetDOM(widget, stats);
        }
      });
    });

    // 3. Update Cart layout summary elements and countdown timers
    if (window.LcPromotionCart) {
      window.LcPromotionCart.updateCartSummary(cart, this.promotions);
    }
  }

  /**
   * Triggers micro-animations and style updates when a promotion is unlocked.
   * @param {object} promo
   */
  triggerUnlockAnimation(promo) {
    const event = new CustomEvent('lc:promotion:unlocked', { detail: promo });
    document.dispatchEvent(event);
    
    // Add flashing overlay effect to the active cart drawer
    const drawerInner = document.querySelector('[data-lc-cart-section="cart-drawer"]');
    if (drawerInner) {
      drawerInner.classList.add('lc-promo-unlocked-flash');
      setTimeout(() => drawerInner.classList.remove('lc-promo-unlocked-flash'), 1200);
    }
  }
}

// Instantiate and bind globally
document.addEventListener('DOMContentLoaded', () => {
  window.LcPromotionEngine = new LcPromotionEngine();
  window.LcPromotionEngine.init();
});
